import { FC, memo } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import ColumnLayout from '@amzn/awsui-components-core/column-layout';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import Checkbox from '@amzn/awsui-components-core/checkbox';
import Textarea from '@amzn/awsui-components-core/textarea';
import Select, { SelectProps } from '@amzn/awsui-components-core/select';
import { useTranslate } from 'src/hooks/useTranslate';
import { SectionBaseProps } from '../../types';

export interface CashbackSectionProps extends SectionBaseProps {
    /** Whether cashback is restricted to Prime members */
    primeRestriction: boolean;
    /** Callback to update the prime restriction */
    setPrimeRestriction: (value: boolean) => void;
    /** Terms and conditions text */
    termsAndConditions: string;
    /** Callback to update terms and conditions */
    setTermsAndConditions: (value: string) => void;
    /** Cashback extent guardrail % (default 100) */
    cashbackExtentGuardrail: string;
    /** Callback to update cashback extent guardrail */
    setCashbackExtentGuardrail: (value: string) => void;
    /** Percent of ASIN price limit % */
    cashbackPercentLimitGuardrail: string;
    /** Callback to update cashback percent limit guardrail */
    setCashbackPercentLimitGuardrail: (value: string) => void;
    /** Absolute limit INR */
    cashbackAbsoluteLimitGuardrail: string;
    /** Callback to update cashback absolute limit guardrail */
    setCashbackAbsoluteLimitGuardrail: (value: string) => void;
    /** Selected rounding option value */
    roundingOption: string;
    /** Callback to update the rounding option */
    setRoundingOption: (value: string) => void;
    /** Rounding option choices (FIVE/TEN/FIFTY/HUNDRED) */
    roundingOptions: SelectProps.Option[];
    /** Inline error for Terms and Conditions */
    termsAndConditionsError?: string;
    /** Inline error for extent guardrail */
    cashbackExtentGuardrailError?: string;
    /** Inline error for percent limit guardrail */
    cashbackPercentLimitGuardrailError?: string;
    /** Inline error for absolute limit guardrail */
    cashbackAbsoluteLimitGuardrailError?: string;
}

/**
 * Cashback section — renders CASHBACK-specific form fields:
 * prime restriction, terms & conditions, guardrails, and rounding.
 * Only shown for CASHBACK campaign type (parent controls visibility).
 * Owns its own i18n bundle (`organisms.cashbackSection`) so translations
 * are local to this component rather than prop-drilled from the page.
 */
const CashbackSection: FC<CashbackSectionProps> = ({
    primeRestriction,
    setPrimeRestriction,
    termsAndConditions,
    setTermsAndConditions,
    cashbackExtentGuardrail,
    setCashbackExtentGuardrail,
    cashbackPercentLimitGuardrail,
    setCashbackPercentLimitGuardrail,
    cashbackAbsoluteLimitGuardrail,
    setCashbackAbsoluteLimitGuardrail,
    roundingOption,
    setRoundingOption,
    roundingOptions,
    mode,
    disabled,
    termsAndConditionsError,
    cashbackExtentGuardrailError,
    cashbackPercentLimitGuardrailError,
    cashbackAbsoluteLimitGuardrailError,
}) => {
    const { translate } = useTranslate('organisms.cashbackSection');
    const isDisabled = disabled ?? mode === 'view';
    const errorI18n = { errorIconAriaLabel: translate('error_icon_aria_label') };

    return (
        <SpaceBetween size={mode === 'view' ? 's' : 'l'}>
            {/* ── Prime restriction ──────────────────────────────── */}
            <Container
                header={
                    <Header variant="h2">{translate('section_cashback_restrictions')}</Header>
                }
            >
                <Checkbox
                    checked={primeRestriction}
                    onChange={({ detail }) => setPrimeRestriction(detail.checked)}
                    disabled={isDisabled}
                    data-testid="prime-restriction"
                >
                    {translate('prime_restriction_label')}
                </Checkbox>
            </Container>

            {/* ── Terms and conditions ───────────────────────────── */}
            <Container
                header={
                    <Header variant="h2">{translate('section_terms_and_conditions')}</Header>
                }
            >
                <FormField label={translate('terms_and_conditions_label')} errorText={termsAndConditionsError} i18nStrings={errorI18n}>
                    <Textarea
                        value={termsAndConditions}
                        onChange={({ detail }) => setTermsAndConditions(detail.value)}
                        placeholder={translate('terms_and_conditions_placeholder')}
                        rows={4}
                        ariaRequired={true}
                        disabled={isDisabled}
                        data-testid="terms-and-conditions"
                        invalid={!!termsAndConditionsError}
                    />
                </FormField>
            </Container>

            {/* ── Guardrails (3-column row) ─────────────────────── */}
            <Container
                header={
                    <Header variant="h2">{translate('section_cashback_guardrails')}</Header>
                }
            >
                <ColumnLayout columns={3}>
                    <FormField label={translate('cashback_extent_guardrail_label')} errorText={cashbackExtentGuardrailError} i18nStrings={errorI18n}>
                        <Input
                            value={cashbackExtentGuardrail}
                            onChange={({ detail }) => setCashbackExtentGuardrail(detail.value)}
                            placeholder="100"
                            type="number"
                            ariaRequired={true}
                            disabled={isDisabled}
                            data-testid="cashback-extent-guardrail"
                            invalid={!!cashbackExtentGuardrailError}
                        />
                    </FormField>
                    <FormField label={translate('cashback_percent_limit_guardrail_label')} errorText={cashbackPercentLimitGuardrailError} i18nStrings={errorI18n}>
                        <Input
                            value={cashbackPercentLimitGuardrail}
                            onChange={({ detail }) => setCashbackPercentLimitGuardrail(detail.value)}
                            type="number"
                            ariaRequired={true}
                            disabled={isDisabled}
                            data-testid="cashback-percent-limit-guardrail"
                            invalid={!!cashbackPercentLimitGuardrailError}
                        />
                    </FormField>
                    <FormField label={translate('cashback_absolute_limit_guardrail_label')} errorText={cashbackAbsoluteLimitGuardrailError} i18nStrings={errorI18n}>
                        <Input
                            value={cashbackAbsoluteLimitGuardrail}
                            onChange={({ detail }) => setCashbackAbsoluteLimitGuardrail(detail.value)}
                            type="number"
                            ariaRequired={true}
                            disabled={isDisabled}
                            data-testid="cashback-absolute-limit-guardrail"
                            invalid={!!cashbackAbsoluteLimitGuardrailError}
                        />
                    </FormField>
                </ColumnLayout>
            </Container>

            {/* ── Rounding ──────────────────────────────────────── */}
            <Container
                header={
                    <Header variant="h2">{translate('section_cashback_rounding')}</Header>
                }
            >
                <FormField label={translate('rounding_option_label')}>
                    <Select
                        selectedOption={
                            roundingOption
                                ? roundingOptions.find((o) => o.value === roundingOption) ?? null
                                : null
                        }
                        onChange={({ detail }) =>
                            setRoundingOption(detail.selectedOption.value ?? '')
                        }
                        options={roundingOptions}
                        placeholder={translate('rounding_option_placeholder')}
                        ariaRequired={true}
                        disabled={isDisabled}
                        data-testid="round-off-value"
                    />
                </FormField>
            </Container>
        </SpaceBetween>
    );
};

export default memo(CashbackSection);
